import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  FaPlane,
  FaGraduationCap,
  FaUsers,
  FaGlobeEurope,
  FaBriefcase,
  FaHeart,
  FaCheckCircle,
  FaClock,
  FaFileAlt,
  FaHeadset,
  FaArrowLeft,
  FaPhone,
  FaWhatsapp,
  FaCalendarAlt,
  FaPassport,
  FaShieldAlt,
  FaMoneyCheckAlt,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaEnvelope,
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const whatsappNumber = '03135993810';
  const countryCode = '92';

  const services = {
    tourist: {
      id: 'tourist',
      icon: <FaPlane className="text-5xl" />,
      title: 'Tourist Visas',
      description: 'Explore destinations worldwide with our comprehensive tourist visa services. Whether you\'re planning a vacation, visiting friends or family, or exploring new cultures, we make the process seamless.',
      detailedDescription: 'Our tourist visa services cover all popular destinations including USA, UK, Canada, Australia, Europe, and many more. We handle everything from document preparation to interview guidance.',
      color: 'from-blue-500 to-blue-700',
      bgColor: 'bg-blue-50',
      features: [
        'Single & Multiple Entry Visas',
        'Express Processing Available',
        'Travel Insurance Assistance',
        'Itinerary & Hotel Booking Support',
        'Visa Extension Services',
        'Interview Preparation',
        'Document Checklist & Review',
        'Application Form Filling Assistance',
      ],
      requirements: [
        'Valid Passport (6+ months validity)',
        'Passport-size photographs',
        'Bank statements (last 6 months)',
        'Travel itinerary',
        'Hotel bookings',
        'Return flight tickets',
        'Travel insurance',
        'Proof of employment/income',
      ],
      processingTime: '3-15 Business Days',
      successRate: '98%',
      priceRange: '$100 - $500',
      popularDestinations: ['USA', 'UK', 'Canada', 'Australia', 'Dubai', 'Thailand', 'Malaysia'],
    },
    student: {
      id: 'student',
      icon: <FaGraduationCap className="text-5xl" />,
      title: 'Student Visas',
      description: 'Achieve your academic dreams with our student visa expertise. We guide you through every step of the process to ensure you can focus on your education.',
      detailedDescription: 'From university selection to post-study work opportunities, we provide comprehensive support for students pursuing education abroad.',
      color: 'from-green-500 to-green-700',
      bgColor: 'bg-green-50',
      features: [
        'University Admission Support',
        'Financial Documentation',
        'Statement of Purpose Review',
        'Post-Study Work Visa Guidance',
        'Dependent Visa Processing',
        'Scholarship Application Assistance',
        'Pre-departure Orientation',
        'Accommodation Assistance',
      ],
      requirements: [
        'University acceptance letter',
        'Financial proof (tuition + living expenses)',
        'Academic transcripts',
        'English proficiency test scores',
        'Statement of Purpose',
        'Passport and photographs',
        'Medical examination report',
        'Proof of funds',
      ],
      processingTime: '4-8 Weeks',
      successRate: '96%',
      priceRange: '$200 - $800',
      popularDestinations: ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Netherlands'],
    },
    schengen: {
      id: 'schengen',
      icon: <FaGlobeEurope className="text-5xl" />,
      title: 'Schengen Visas',
      description: 'Travel across 26 European countries with a single visa. Perfect for tourism, business, or visiting friends and family in Europe.',
      detailedDescription: 'We specialize in Schengen visa applications for all member countries, ensuring the highest chances of approval.',
      color: 'from-purple-500 to-purple-700',
      bgColor: 'bg-purple-50',
      features: [
        'Multiple Country Itineraries',
        'Travel Medical Insurance',
        'Hotel & Flight Bookings',
        'Business Invitation Letters',
        'Visa Type Selection Guidance',
        'Cover Letter Preparation',
        'Appointment Booking',
        'Document Translation Services',
      ],
      requirements: [
        'Completed Schengen visa application form',
        'Valid passport',
        'Passport photographs',
        'Travel insurance (minimum €30,000 coverage)',
        'Flight itinerary',
        'Hotel reservations',
        'Proof of financial means',
        'Employment/business proof',
      ],
      processingTime: '10-20 Business Days',
      successRate: '95%',
      priceRange: '$80 - $350',
      popularDestinations: ['France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Greece'],
    },
    family: {
      id: 'family',
      icon: <FaUsers className="text-5xl" />,
      title: 'Family Visas',
      description: 'Reunite with your loved ones across borders. We help families stay together by navigating complex immigration processes.',
      detailedDescription: 'Specialized in spouse, parent, child, and dependent visa applications for various countries.',
      color: 'from-pink-500 to-pink-700',
      bgColor: 'bg-pink-50',
      features: [
        'Spouse & Partner Visas',
        'Parent & Child Visas',
        'Document Verification',
        'Relationship Evidence Preparation',
        'Permanent Residence Pathway',
        'Medical Examination Coordination',
        'Police Clearance Assistance',
        'Interview Preparation',
      ],
      requirements: [
        'Proof of relationship (marriage/birth certificates)',
        'Sponsor\'s financial documents',
        'Accommodation proof',
        'Medical certificates',
        'Police clearance certificates',
        'Sponsorship undertaking',
        'Relationship evidence (photos, communication records)',
        'Passport and photographs',
      ],
      processingTime: '3-12 Months',
      successRate: '92%',
      priceRange: '$300 - $1500',
      popularDestinations: ['USA', 'UK', 'Canada', 'Australia', 'New Zealand', 'UAE'],
    },
    business: {
      id: 'business',
      icon: <FaBriefcase className="text-5xl" />,
      title: 'Business Visas',
      description: 'Expand your business globally with our visa support. Perfect for meetings, conferences, and exploring business opportunities.',
      detailedDescription: 'We facilitate business travel for entrepreneurs, investors, and corporate professionals.',
      color: 'from-orange-500 to-orange-700',
      bgColor: 'bg-orange-50',
      features: [
        'Business Invitation Letters',
        'Conference & Meeting Visas',
        'Investment Visas',
        'Corporate Documentation',
        'Multiple Entry Long-Term Visas',
        'Company Registration Support',
        'Market Research Assistance',
        'Networking Event Coordination',
      ],
      requirements: [
        'Business invitation letter',
        'Company registration documents',
        'Financial statements',
        'Business plan (for investors)',
        'Conference registration proof',
        'Letter from employer',
        'Previous business travel records',
        'Proof of business activities',
      ],
      processingTime: '5-15 Business Days',
      successRate: '97%',
      priceRange: '$150 - $600',
      popularDestinations: ['USA', 'UK', 'China', 'UAE', 'Singapore', 'Germany', 'Japan'],
    },
    medical: {
      id: 'medical',
      icon: <FaHeart className="text-5xl" />,
      title: 'Medical Visas',
      description: 'Access world-class healthcare facilities abroad. We coordinate medical visas for patients seeking specialized treatment.',
      detailedDescription: 'Complete assistance for medical tourism, including treatment coordination and post-care support.',
      color: 'from-red-500 to-red-700',
      bgColor: 'bg-red-50',
      features: [
        'Medical Appointment Letters',
        'Treatment Cost Estimates',
        'Medical History Documentation',
        'Attendant Visa Processing',
        'Follow-up Treatment Visas',
        'Hospital Coordination',
        'Medical Second Opinion',
        'Post-treatment Care Planning',
      ],
      requirements: [
        'Medical diagnosis reports',
        'Doctor\'s recommendation',
        'Treatment cost estimate',
        'Hospital appointment letter',
        'Financial proof for treatment',
        'Medical insurance',
        'Attendant\'s documents',
        'Passport and photographs',
      ],
      processingTime: '7-14 Business Days',
      successRate: '94%',
      priceRange: '$100 - $400',
      popularDestinations: ['India', 'Thailand', 'Turkey', 'Germany', 'USA', 'UK', 'Singapore'],
    },
  };

  const service = services[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Service Not Found</h2>
          <p className="text-gray-600 mb-6">The service you're looking for doesn't exist.</p>
          <Link to="/services" className="btn-primary">
            View All Services
          </Link>
        </div>
      </div>
    );
  }

  const handleEnquiryChange = (e) => {
    setEnquiryData({
      ...enquiryData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const message = `*Service Enquiry - ${service.title}*%0A%0A
*Name:* ${enquiryData.name}%0A
*Email:* ${enquiryData.email}%0A
*Phone:* ${enquiryData.phone}%0A
*Service:* ${service.title}%0A
*Message:* ${enquiryData.message}%0A%0A
_I'm interested in this service and would like more information._`;

    const whatsappURL = `https://wa.me/${countryCode}${whatsappNumber}?text=${message}`;
    window.open(whatsappURL, '_blank');

    setTimeout(() => {
      setEnquiryData({
        name: '',
        email: '',
        phone: '',
        service: service.title,
        message: '',
      });
      setShowEnquiryForm(false);
      setLoading(false);
    }, 1000);
  };

  const handleDirectWhatsApp = () => {
    const message = `*I'm interested in ${service.title}*%0A%0A
Please provide me with more information about this service.`;
    
    const whatsappURL = `https://wa.me/${countryCode}${whatsappNumber}?text=${message}`;
    window.open(whatsappURL, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className={`bg-gradient-to-r ${service.color} text-white py-16`}>
        <div className="container-padding">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/services')}
            className="flex items-center gap-2 text-white hover:text-primary-200 mb-8"
          >
            <FaArrowLeft />
            Back to All Services
          </motion.button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 rounded-xl bg-white/20 backdrop-blur-sm">
                  {service.icon}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold">{service.title}</h1>
              </div>
              <p className="text-xl mb-8 text-white/90">{service.description}</p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowEnquiryForm(true)}
                  className="btn-primary bg-white text-gray-800 hover:bg-gray-100"
                >
                  Get Free Consultation
                </button>
                <button
                  onClick={handleDirectWhatsApp}
                  className="btn-primary border-white text-white hover:bg-white/20 flex items-center gap-2"
                >
                  <FaWhatsapp />
                  WhatsApp Inquiry
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <FaClock className="text-3xl mx-auto mb-3 text-white/80" />
                  <div className="text-sm text-white/70">Processing Time</div>
                  <div className="text-2xl font-bold">{service.processingTime}</div>
                </div>
                <div className="text-center">
                  <FaCheckCircle className="text-3xl mx-auto mb-3 text-white/80" />
                  <div className="text-sm text-white/70">Success Rate</div>
                  <div className="text-2xl font-bold">{service.successRate}</div>
                </div>
                <div className="text-center">
                  <FaMoneyCheckAlt className="text-3xl mx-auto mb-3 text-white/80" />
                  <div className="text-sm text-white/70">Price Range</div>
                  <div className="text-2xl font-bold">{service.priceRange}</div>
                </div>
                <div className="text-center">
                  <FaShieldAlt className="text-3xl mx-auto mb-3 text-white/80" />
                  <div className="text-sm text-white/70">Visa Type</div>
                  <div className="text-xl font-bold">{service.title}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-padding py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column - Features & Details */}
          <div className="lg:col-span-2">
            {/* Detailed Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${service.bgColor} rounded-2xl p-8 mb-8`}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Overview</h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {service.detailedDescription}
              </p>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-8 mb-8"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                What We Offer
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Requirements
              </h2>
              <div className="space-y-4">
                {service.requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <FaFileAlt className="text-primary-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{requirement}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column - Sidebar */}
          <div>
            {/* Popular Destinations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8 mb-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Popular Destinations
              </h3>
              <div className="space-y-3">
                {service.popularDestinations.map((destination, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg">
                    <FaMapMarkerAlt className="text-primary-600" />
                    <span className="text-gray-700">{destination}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Quick Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-primary-900 to-primary-700 text-white rounded-2xl shadow-lg p-8"
            >
              <h3 className="text-2xl font-bold mb-6">Need Help?</h3>
              <p className="mb-6 text-primary-100">
                Get instant assistance from our visa experts
              </p>
              
              <div className="space-y-4 mb-8">
                <a
                  href={`tel:+${countryCode}${whatsappNumber}`}
                  className="flex items-center gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition"
                >
                  <FaPhone className="text-xl" />
                  <span>Call: +{countryCode} {whatsappNumber}</span>
                </a>
                <button
                  onClick={handleDirectWhatsApp}
                  className="w-full flex items-center justify-center gap-3 p-3 bg-green-600 rounded-lg hover:bg-green-700 transition"
                >
                  <FaWhatsapp className="text-xl" />
                  <span>Chat on WhatsApp</span>
                </button>
              </div>

              <div className="text-center text-primary-100 text-sm">
                <FaClock className="inline-block mr-2" />
                Available 24/7 for urgent inquiries
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Enquiry Form Modal */}
      {showEnquiryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-8"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Enquire About {service.title}</h3>
              <button
                onClick={() => setShowEnquiryForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimesCircle className="text-2xl" />
              </button>
            </div>

            <form onSubmit={handleEnquirySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={enquiryData.name}
                  onChange={handleEnquiryChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={enquiryData.email}
                  onChange={handleEnquiryChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={enquiryData.phone}
                  onChange={handleEnquiryChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+92 313 5993810"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  name="message"
                  value={enquiryData.message}
                  onChange={handleEnquiryChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full btn-primary flex items-center justify-center gap-2 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Redirecting to WhatsApp...
                  </>
                ) : (
                  <>
                    <FaWhatsapp />
                    Send via WhatsApp
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;